import React from 'react';
import { Link } from 'react-router-dom';
import { DocPage, Note, Steps, WqTable, CopyValue } from '../../components/documentation/web-integration';

// Test cards for Test mode. Edit here to update the table.
const TEST_CARDS = [
    { brand: 'Verve', pan: '5061050254756707864', expiry: '06/26', cvv: '111', pin: '1111', otp: '', scenario: 'Success' },
    { brand: 'Verve', pan: '5060990580000217499', expiry: '03/50', cvv: '111', pin: '1111', otp: '', scenario: 'Success' },
    { brand: 'Visa', pan: '4000000000002503', expiry: '03/50', cvv: '111', pin: '1111', otp: '', scenario: 'Success' },
    { brand: 'Mastercard', pan: '5123450000000008', expiry: '01/39', cvv: '100', pin: '1111', otp: '123456', scenario: 'Success' },
];

const CONTENTS = ['Test cards', 'How to make a test payment'];

const HOW_TO = [
    <>
        <strong>Switch your dashboard to Test mode</strong> with the Test / Live toggle at the top. New accounts are
        already in Test mode.
    </>,
    <>
        <strong>Start a payment</strong> using your test secret key (it starts with <code>WAYASECK_TEST_</code>), or
        open a payment link you created in Test mode.
    </>,
    <>
        On the WayaQuick checkout, choose <strong>Card</strong> and enter a card number, expiry date and CVV from the
        table.
    </>,
    <>
        Enter the <strong>PIN</strong> when asked, and the <strong>OTP</strong> if the card has one.
    </>,
    <>
        <strong>Check the result</strong>: the payment shows as successful in your dashboard under Transactions, and
        your webhook receives a <code>SUCCESSFUL</code> event.
    </>,
];

const cardRows = TEST_CARDS.map((card) => [
    <strong>{card.brand}</strong>,
    <CopyValue value={card.pan} />,
    card.expiry,
    card.cvv,
    card.pin,
    card.otp ? <code>{card.otp}</code> : <span className="wq-type">Not asked</span>,
    card.scenario,
]);

function TestCard() {
    return (
        <DocPage
            seo={{
                title: 'Test Card Information',
                description:
                    'Test cards (Verve, Visa, Mastercard) with PIN, CVV and OTP for simulating payments in WayaQuick test mode.',
            }}
            eyebrow="Developers · Test mode"
            title={
                <>
                    WayaQuick — <em>Test Card Information</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · Test Card Information · These cards only work in Test mode."
            prev={{ to: '/standard-api-integration', label: 'Standard API Integration' }}
            next={{ to: '/get-started', label: 'Get Started' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">Test cards</h2>
                <p className="wq-lead">
                    Use these cards to make card payments in Test mode, so you can check your integration end to end
                    without spending real money. Click a card number to copy it.
                </p>

                <WqTable
                    columns={['Brand', 'Card number', 'Expiry', 'CVV', 'PIN', 'OTP', 'Result']}
                    rows={cardRows}
                />

                <Note label="Test mode only" tone="critical">
                    These cards only work in Test mode, with a test secret key. In Live mode they are declined, and no
                    real card should ever be used for testing.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">How to make a test payment</h2>
                <Steps items={HOW_TO} />
                <p className="wq-p">
                    To see how your integration handles a payment that doesn't go through, close the checkout without
                    paying. New to Test mode? Start with the{' '}
                    <Link to="/test-environment" className="wq-link">
                        Test Environment
                    </Link>{' '}
                    guide.
                </p>
            </section>
        </DocPage>
    );
}

export default TestCard;
